import { useMemo, useState } from 'react';
import { useIsSSR } from 'react-aria';

import styles from './SelectBase.module.scss';

import {
  Provider,
  Select as ReactAriaSelect,
  type SelectProps as ReactAriaSelectProps,
} from 'react-aria-components';

import { Description, DescriptionContext } from '../Description/Description';
import { Label, LabelContext } from '../Label/Label';
import { mergeClassNames } from '../utils/mergeClassNames';
import { ListBox, ListBoxContext } from './ListBox';
import { Popover, PopoverContext } from './Popover';
import { SelectContext } from './SelectContext';
import { SelectValueBase, SelectValueContext } from './SelectValue';
import { TriggerButton, TriggerButtonContext } from './TriggerButton';

export type Key = string | number;

export type SelectBaseProps<T extends object = object> =
  ReactAriaSelectProps<T> & {
    /**
     * The size of the Select button
     * @default 'medium'
     */
    size?: 'compact' | 'medium' | 'large';
  };

const defaultProps: SelectBaseProps<object> = {
  isDisabled: false,
  isRequired: false,
  size: 'medium',
};

/**
 * `BaseSelect` extends react-aria's
 * [Select](https://react-spectrum.adobe.com/react-aria/Select.html). It
 * provides context for the different components that are used to build the
 * Select component:
 *
 * - `Description`
 * - `Label`
 * - `ListBox`
 * - `Popover`
 * - `TriggerButton`
 * - `Value`
 *
 * `BaseSelect` is the foundation for the main `Select` component. Most use
 * cases should be covered by the `Select` component, but you may need to use
 * `BaseSelect` if you need to heavily customize the `Select`.
 */

export const SelectBase = <T extends object = object>({
  className,
  ...props
}: SelectBaseProps<T>) => {
  const propsWithDefaults = { ...defaultProps, ...props };
  const {
    defaultSelectedKey,
    isDisabled = false,
    isInvalid = false,
    isRequired = false,
    onSelectionChange,
    selectedKey,
    size = 'medium',
  } = propsWithDefaults;

  // Props shared with children via context
  const descriptionProps = { isCompact: size === 'compact', isDisabled };
  const labelProps = {
    isCompact: size === 'compact',
    isDisabled,
    isInvalid,
    isRequired,
  };
  const listBoxProps = { size };
  const popoverProps = { size };
  const selectValueProps = { size };
  const triggerButtonProps = { size };

  const isSSR = useIsSSR();
  const [disabledKeys, setDisabledKeys] = useState<Iterable<Key>>([]);

  const selectContextValue = useMemo(
    () => ({
      registerDisabledItem: (id: Key) => {
        setDisabledKeys((prev) => [...prev, id]);
      },
      size: size,
    }),
    [size]
  );

  if (isSSR) {
    // react-aria Select renders hidden children in order to build the
    // collection even when the popover is not open. react-aria Hidden wrapps
    // its children in a `template` tag in SSR environments. The presence of
    // that `template` creates errors during hydration.
    //
    // https://github.com/adobe/react-spectrum/blob/17751bb75cf4b0500e86abe9e1b4561d140f9486/packages/react-aria-components/src/Select.tsx#L135
    //
    // https://github.com/adobe/react-spectrum/blob/main/packages/react-aria-components/src/utils.tsx#L326
    //
    // We return nothing while the component is being server side rendered or
    // hydrated on the client to prevent that error.
    return null;
  }

  return (
    <Provider
      values={[
        [DescriptionContext, descriptionProps],
        [LabelContext, labelProps],
        [ListBoxContext, listBoxProps],
        [PopoverContext, popoverProps],
        [SelectContext, selectContextValue],
        [SelectValueContext, selectValueProps],
        [TriggerButtonContext, triggerButtonProps],
      ]}
    >
      <ReactAriaSelect
        {...props}
        className={(renderProps) =>
          mergeClassNames([styles.selectBase, className], renderProps)
        }
        data-size={size}
        data-zds
        defaultSelectedKey={defaultSelectedKey}
        disabledKeys={disabledKeys}
        onSelectionChange={onSelectionChange}
        isInvalid={isInvalid}
        selectedKey={selectedKey}
      />
    </Provider>
  );
};

/**
 * A styled description for a Select. Useful when creating a custom Select.
 *
 * Merges context props from `DescriptionContext` with local props (preferring local props).
 */
SelectBase.Description = Description;
/**
 * A styled Button to use as a trigger for a Select from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `TriggerButtonContext` with local props (preferring local props).
 */
SelectBase.TriggerButton = TriggerButton;
/**
 * A styled ListBox from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `ListBoxContext` with local props (preferring local props).
 */
SelectBase.ListBox = ListBox;
/**
 * A styled Popover from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `PopoverContext` with local props (preferring local props).
 */
SelectBase.Popover = Popover;
/**
 * A styled SelectValue from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `SelectValueContext` with local props (preferring local props).
 */
SelectBase.Value = SelectValueBase;
/**
 * A styled label for a Select. Useful when creating a custom Select.
 *
 * Merges context props from `LabelContext` with local props (preferring local props).
 */
SelectBase.Label = Label;
